import { api } from '../api/client';

export interface SessionData {
  sessionId: string;
  metrics: any;
  history: any[];
  timestamp: number;
  retryCount?: number;
}

const STORAGE_KEY = 'pending_sessions';
const MAX_RETRIES = 5;

export type SyncStatus = 'idle' | 'syncing' | 'offline' | 'error';

export interface SyncState {
  status: SyncStatus;
  pendingCount: number;
  lastSyncedAt: number | null;
}

let currentStatus: SyncStatus = navigator.onLine ? 'idle' : 'offline';
let lastSyncedAt: number | null = null;

const notifyStateChange = () => {
  window.dispatchEvent(new CustomEvent('sync-state-changed', {
    detail: {
      status: currentStatus,
      pendingCount: SyncQueue.getQueueSize(),
      lastSyncedAt
    }
  }));
};

window.addEventListener('online', () => {
  currentStatus = 'idle';
  notifyStateChange();
  SyncQueue.processQueue();
});

window.addEventListener('offline', () => {
  currentStatus = 'offline';
  notifyStateChange();
});

export const SyncQueue = {
  getPendingSessions: (): SessionData[] => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      console.error('Failed to read pending sessions', e);
      return [];
    }
  },

  getQueueSize: (): number => {
    return SyncQueue.getPendingSessions().length;
  },

  getSyncState: (): SyncState => ({
    status: currentStatus,
    pendingCount: SyncQueue.getQueueSize(),
    lastSyncedAt
  }),

  queueSession: (session: SessionData) => {
    try {
      const list = SyncQueue.getPendingSessions();
      // Avoid duplicates based on sessionId
      const exists = list.some(s => s.sessionId === session.sessionId);
      if (exists) {
        // Replace to be safe in case of re-queueing with better data.
        const updated = list.map(s => s.sessionId === session.sessionId ? { ...session, retryCount: 0 } : s);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
        notifyStateChange();
        return;
      }
      session.retryCount = 0;
      list.push(session);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
      notifyStateChange();
      // Try to process immediately if online
      if (navigator.onLine) {
        setTimeout(() => SyncQueue.processQueue(), 100);
      }
    } catch (e) {
      console.error('Failed to queue session', e);
    }
  },

  processQueue: async () => {
    if (!navigator.onLine) {
      currentStatus = 'offline';
      notifyStateChange();
      return;
    }
    
    const list = SyncQueue.getPendingSessions();
    if (list.length === 0) {
      currentStatus = 'idle';
      notifyStateChange();
      return;
    }

    currentStatus = 'syncing';
    notifyStateChange();

    const remaining: SessionData[] = [];
    let changed = false;

    for (const session of list) {
      try {
        await api.post(`/session/${session.sessionId}/sync`, {
          metrics: session.metrics,
          history: session.history
        }, { skipAuthRedirect: true } as any);
        // Success: item is NOT added to remaining
        changed = true;
      } catch (e: any) {
        const status = e.response?.status;
        
        // Conflict Resolution Strategy:
        // 404 (Not Found): Session deleted on server. Drop it.
        // 409 (Conflict): Server has newer data. For metrics, we assume server wins or specific merge logic needed.
        // For now, we drop on 409 to prevent blocking the queue, assuming server state is "correct".
        if (status === 404 || status === 409) {
           console.warn(`Dropping session ${session.sessionId} due to ${status} (Conflict/Gone)`);
           changed = true;
           continue; 
        }

        // Drop on other client errors (4xx) as retrying won't fix them
        if (status && status >= 400 && status < 500) {
           console.warn(`Dropping session ${session.sessionId} from sync queue due to ${status} error`);
           changed = true; 
           continue; 
        }

        // Keep in queue on server errors (5xx) or network errors
        const retries = (session.retryCount || 0) + 1;
        if (retries > MAX_RETRIES) {
            console.error(`Dropping session ${session.sessionId} after ${MAX_RETRIES} retries`);
            changed = true;
            continue;
        }
        
        remaining.push({ ...session, retryCount: retries });
        if (retries !== session.retryCount) changed = true;
      }
    }

    if (changed) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(remaining));
      } catch (e) {
        console.error('Failed to update pending sessions', e);
      }
    }
    
    // Update status
    if (remaining.length === 0) {
      currentStatus = 'idle';
      lastSyncedAt = Date.now();
    } else {
      // If items remain, we are either still trying or waiting for next retry
      // We'll set to error state if we have items that failed
      currentStatus = 'error'; 
    }
    notifyStateChange();
  }
};
